import { useState, useEffect, useCallback, RefObject } from 'react'

/**
 * Observes whether the hover-column header is inside the scroll container's viewport.
 *
 * Returns a single boolean instead of per-row tracking — the column is either
 * visible for all rows or hidden for all rows.
 *
 * Uses IntersectionObserver (root = scroll container) so there are zero
 * getBoundingClientRect calls on scroll.
 */
export function useHoverColumnInView(scrollRef: RefObject<HTMLDivElement | null>) {
  const [isInView, setIsInView] = useState(true)
  const [headerEl, setHeaderEl] = useState<HTMLTableCellElement | null>(null)

  // Callback ref for the hover column <th>
  const hoverColumnHeaderRef = useCallback((el: HTMLTableCellElement | null) => {
    setHeaderEl(el)
  }, [])

  useEffect(() => {
    const root = scrollRef.current
    if (!root || !headerEl) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry) return
        // Column is "in view" when fully visible (ratio ~1)
        // threshold 0.9 gives a small tolerance for sub-pixel rounding
        setIsInView(entry.intersectionRatio >= 0.9)
      },
      { root, threshold: [0, 0.9] },
    )

    observer.observe(headerEl)

    return () => observer.disconnect()
  }, [scrollRef, headerEl])

  return { isHoverColumnInView: isInView, hoverColumnHeaderRef }
}
